/**
 * Editor for a single ProgramDocumentSection.
 *
 * Canonical content is markdown; Quill shows HTML. On load: markdown → HTML
 * via `marked`. On save (debounced 1.5s): HTML → markdown via `turndown`
 * and PATCHed to the section endpoint, which flips status to 'edited' and
 * attributes the edit to the analyst.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { ChevronRight, Lock, Unlock, RefreshCw, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../../components/ui/Collapsible'
import {
  type ProgramDocumentSection,
  GenerationConflictError,
  regenerateSection,
  updateProgramSection,
  uploadAsset,
} from '../../components/document-templates/api'
import type { ResolvedSection, SkillManifest } from '../../components/document-templates/types'
import {
  MARKDOWN_SAFE_TOOLBAR,
  htmlToMarkdown,
  markdownToHtml,
} from '../../lib/markdownEditor'

interface Props {
  section: ProgramDocumentSection
  programDocumentId: string
  /** The same section resolved from the effective template, matched on
   *  `section_key`. Absent while the template is still loading. */
  resolvedSection?: ResolvedSection | null
  /** Writing-skill manifests; `shows_questions` decides what this section
   *  advertises as its source. Absent while still loading. */
  skillManifests?: SkillManifest[] | null
  onChange?: (updated: ProgramDocumentSection) => void
  onRegenerateQueued?: () => void
}

/** Skill assumed when a section declares none. Mirrors the orchestrator's
 *  `leaf.resolve("writing_skill") or DEFAULT_SKILL`
 *  (backend/apps/documents/services/synthesis_orchestrator.py), so a null
 *  skill is treated as question-based here exactly as it is at generation. */
const DEFAULT_SKILL = 'question_based_section'

export type SectionSource =
  | { kind: 'questions'; prompts: string[] }
  | { kind: 'description'; description: string }

/**
 * Decide what a section should advertise as the source of its content.
 *
 * A "special" section is one whose writing skill composes content from
 * something other than the answers to its own attached ProgramSpecs — a
 * sibling document, upstream sections, the solution taxonomy, vendor
 * records. `SkillManifest.shows_questions` is the registry's declaration of
 * exactly that distinction, so it is the discriminator. When a skill key has
 * no manifest we fall back to comparing against DEFAULT_SKILL, matching the
 * console template editor (components/document-templates/SectionEditor.tsx).
 *
 * Per Ryan: a spec with a blank prompt is ignored outright. If that empties
 * the list, the section advertises nothing rather than showing a bare lead-in.
 */
export function resolveSectionSource(
  resolved: ResolvedSection | null | undefined,
  manifests: SkillManifest[] | null | undefined,
): SectionSource | null {
  if (!resolved) return null

  const skillKey = resolved.writing_skill ?? DEFAULT_SKILL
  const manifest = manifests?.find((m) => m.key === skillKey)
  const showsQuestions = manifest ? manifest.shows_questions : skillKey === DEFAULT_SKILL

  if (showsQuestions) {
    const prompts = (resolved.questions ?? [])
      .map((q) => (q.prompt ?? '').trim())
      .filter((p) => p.length > 0)
    return prompts.length > 0 ? { kind: 'questions', prompts } : null
  }

  const description = (resolved.description ?? '').trim()
  return description.length > 0 ? { kind: 'description', description } : null
}

/** Unobtrusive, slightly greyed note naming where a section's content comes
 *  from. Sits above the editor so it reads as a caption, not a panel. */
function SectionSourceNote({ source }: { source: SectionSource }) {
  return (
    <div className="px-5 py-2.5 border-b border-border bg-muted/20 text-xs text-muted-foreground">
      {source.kind === 'questions' ? (
        <>
          <p>This document section answers the following questions:</p>
          <ul className="mt-1 space-y-0.5 list-disc pl-4">
            {source.prompts.map((prompt, i) => (
              <li key={i}>{prompt}</li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <p>What this document section covers:</p>
          <p className="mt-1">{source.description}</p>
        </>
      )}
    </div>
  )
}

const STATUS_BADGE: Record<ProgramDocumentSection['status'], { label: string; variant: 'pending' | 'in-progress' | 'success' | 'info' | 'warning' | 'destructive' | 'default' }> = {
  pending: { label: 'Pending', variant: 'pending' },
  generating: { label: 'Generating…', variant: 'in-progress' },
  generated: { label: 'Generated', variant: 'success' },
  edited: { label: 'Edited', variant: 'info' },
  stale: { label: 'Stale', variant: 'warning' },
  failed: { label: 'Failed', variant: 'destructive' },
}

export default function SectionEditor({
  section,
  programDocumentId,
  resolvedSection,
  skillManifests,
  onChange,
  onRegenerateQueued,
}: Props) {
  const [html, setHtml] = useState<string>(() => markdownToHtml(section.content_markdown))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Sections start collapsed so the document reads as a scannable outline; the
  // user unfolds the ones they want to edit. The Quill editor is only mounted
  // while a section is open (Radix unmounts collapsed content).
  const [open, setOpen] = useState(false)
  const quillRef = useRef<ReactQuill | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestHtmlRef = useRef(html)

  // Re-initialize when the underlying section content changes (e.g., post-regen).
  useEffect(() => {
    const next = markdownToHtml(section.content_markdown)
    setHtml(next)
    latestHtmlRef.current = next
  }, [section.content_markdown])

  const imageHandler = useCallback(async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const asset = await uploadAsset(programDocumentId, file)
        const quill = quillRef.current?.getEditor()
        if (quill) {
          const range = quill.getSelection(true)
          quill.insertEmbed(range.index, 'image', asset.file_url, 'user')
          quill.setSelection(range.index + 1, 0)
        }
      } catch (e: any) {
        setError(e?.message || 'Image upload failed')
      }
    }
    input.click()
  }, [programDocumentId])

  const modules = useMemo(
    () => ({
      toolbar: {
        container: MARKDOWN_SAFE_TOOLBAR,
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler],
  )

  const persist = useCallback(async () => {
    const nextMarkdown = htmlToMarkdown(latestHtmlRef.current)
    if (nextMarkdown === (section.content_markdown || '').trim()) return
    setSaving(true)
    setError(null)
    try {
      const updated = await updateProgramSection(section.id, { content_markdown: nextMarkdown })
      onChange?.(updated)
    } catch (e: any) {
      setError(e?.message || 'Autosave failed')
    } finally {
      setSaving(false)
    }
  }, [section.id, section.content_markdown, onChange])

  const onEditorChange = (value: string) => {
    if (section.locked) return
    setHtml(value)
    latestHtmlRef.current = value
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(persist, 1500)
  }

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  const toggleLock = async () => {
    try {
      const updated = await updateProgramSection(section.id, { locked: !section.locked })
      onChange?.(updated)
    } catch (e: any) {
      setError(e?.message || 'Lock toggle failed')
    }
  }

  const handleRegenerate = async () => {
    if (section.locked) {
      if (!confirm('This section is locked. Regenerate anyway (overwrites your edits)?')) return
    }
    try {
      await regenerateSection(programDocumentId, section.section_key, {
        force: section.locked,
      })
      onRegenerateQueued?.()
    } catch (e: any) {
      if (e instanceof GenerationConflictError) {
        setError(e.message)
      } else {
        setError(e?.message || 'Regenerate failed')
      }
    }
  }

  const statusBadge = STATUS_BADGE[section.status]
  // "Done" sections carry no useful badge — the check icon is enough. Only the
  // attention states (pending / generating / stale / failed) get a badge.
  const isDone = section.status === 'generated' || section.status === 'edited'
  const lastGen = section.last_generated_at ? new Date(section.last_generated_at).toLocaleString() : '—'
  const lastEdit = section.last_edited_at ? new Date(section.last_edited_at).toLocaleString() : '—'
  const skill = (section.generation_metadata?.skill as string) || '—'
  const sectionSource = resolveSectionSource(resolvedSection, skillManifests)

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="bg-card border border-border rounded-lg overflow-hidden"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={`flex w-full items-center justify-between gap-4 px-5 py-3 bg-muted/30 text-left transition-colors hover:bg-muted/50 ${open ? 'border-b border-border' : ''}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <ChevronRight className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
            <span className="text-xs text-muted-foreground font-mono">{String(section.order).padStart(2, '0')}</span>
            <h2 className="text-lg font-semibold text-foreground truncate">{section.title}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {isDone ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            ) : (
              <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
            )}
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {error && (
          <div className="flex items-center gap-2 px-5 py-2 bg-destructive/10 text-destructive text-sm">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}
        {section.status === 'stale' && (
          <div className="flex items-center gap-2 px-5 py-2 bg-amber-500/10 text-amber-900 dark:text-amber-200 text-sm border-b border-border">
            <AlertTriangle className="w-4 h-4" />
            Upstream data changed — regenerate to refresh this section.
          </div>
        )}
        {section.status === 'failed' && section.error && (
          <div className="flex items-center gap-2 px-5 py-2 bg-destructive/10 text-destructive text-sm border-b border-border">
            <AlertTriangle className="w-4 h-4" />
            {section.error}
          </div>
        )}
        {sectionSource && <SectionSourceNote source={sectionSource} />}
        <div className="quill-flush">
          <ReactQuill
            ref={(el) => { quillRef.current = el }}
            value={html}
            onChange={onEditorChange}
            modules={modules}
            readOnly={section.locked}
            theme="snow"
          />
        </div>
        <footer className="flex items-center justify-between gap-3 px-5 py-2 border-t border-border bg-muted/30 text-xs text-muted-foreground">
          <div className="min-w-0 truncate">
            Skill: <span className="font-mono">{skill}</span>
            {' · '}
            Last generated: {lastGen}
            {' · '}
            Last edit: {lastEdit} ({section.last_edited_by ?? '—'})
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={toggleLock}>
              {section.locked ? <Unlock className="w-4 h-4 mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
              {section.locked ? 'Unlock' : 'Lock'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleRegenerate} disabled={section.status === 'generating'}>
              <RefreshCw className={`w-4 h-4 mr-1 ${section.status === 'generating' ? 'animate-spin' : ''}`} />
              Regenerate
            </Button>
          </div>
        </footer>
      </CollapsibleContent>
    </Collapsible>
  )
}
